import type { DestroyOptions } from 'pixi.js';
import { Container, Sprite } from 'pixi.js';
import type { AppStateController } from '../app/state/AppStateController';
import { LivesCounterView } from '../game/views/common/counters/LivesCounterView';
import { CoinCounterView } from '../game/views/common/counters/CoinCounterView';
import { registerResizableObject } from '../utils/pixi/registerResizableObject';
import { setContainerParent } from '../utils/pixi/transform-utils';
import { getSafeAreaInset } from '../safearea/SafeAreaProvider';

export class MainMenuTopBarView extends Container {
  public readonly moneyCounter: CoinCounterView;

  public readonly livesCounter: LivesCounterView;

  public readonly settingsButton: Sprite;

  constructor(
    private readonly state: AppStateController,
    iapAvailable: boolean,
  ) {
    super();

    this.moneyCounter = new CoinCounterView(state, iapAvailable).props({
      parent: this,
      x: 14,
      y: 21,
    });

    this.livesCounter = new LivesCounterView(state).props({
      parent: this,
      x: 258,
      y: 21,
    });

    this.settingsButton = Sprite.from('settings').props({
      parent: this,
      scale: 0.95,
      anchor: 0.5,
    });
    this.settingsButton.interactive = true;
    this.settingsButton.cursor = 'pointer';

    registerResizableObject(this);
  }

  public setCountersTempParent(tempParent: Container): void {
    setContainerParent(this.moneyCounter, tempParent);
    setContainerParent(this.livesCounter, tempParent);
  }

  public resetCountersParent(): void {
    setContainerParent(this.moneyCounter, this);
    setContainerParent(this.livesCounter, this);
  }

  public async hide(): Promise<void> {
    this.interactiveChildren = false;
    if (this.alpha !== 0) {
      await this.tweenTo({ alpha: 0 }, 200, 'sinIn', true).asyncOnComplete();
    }
  }

  public async show(): Promise<void> {
    this.interactiveChildren = true;
    if (this.alpha !== 1) {
      await this.tweenTo({ alpha: 1 }, 200, 'sinIn', true).asyncOnComplete();
    }
  }

  public destroy(options?: DestroyOptions): void {
    // Ensures destruction event if counters re-parented.
    this.moneyCounter.destroy(options);
    this.livesCounter.destroy(options);
    super.destroy(options);
  }

  public resize(width: number): void {
    const isHorizontal = this.state.isHorizontalLayoutActive();
    const safeArea = getSafeAreaInset();

    // Adjust money counter position for safe area
    this.moneyCounter.props({
      x: 14 + safeArea.left,
      y: 21 + safeArea.top,
    });

    // Adjust lives counter position for safe area
    this.livesCounter.props({
      x: 258 + safeArea.left,
      y: 21 + safeArea.top,
    });

    this.settingsButton.props({
      x: width - 53 - safeArea.right,
      y: 62 + safeArea.top,
      visible: !isHorizontal,
    });
  }
}
